import React, { useEffect, useState } from 'react';
import {
    Grid2,
    Box,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../../../Components/CustomIcon';
import CloseButton from '../../../Components/CloseButton';
import { IDDisplayBox } from '../../../Theme/DigitalTwinStyles';
import { Divider } from 'antd';
import FullScreenDialog from '../../../Components/FullScreenDialog';
import CustomTag from '../../../Components/CustomTag';
import { getMetricTimeStamp } from '../../../Helper/DatePicker/DateConverter';
import { alertTagStyles } from '../../../Helper/DigitalTwin/DataFile';
import { getFormattedResolutionTime } from '../../../Helper/DatePicker/DateFormatters';
import DotSeparator from '../../../Components/DotSeparator';
import { toTitleCase } from '../../../Helper/Formatters/TextFormatter';
import { getDiagnosticInfo } from '../../../Api/BmsFaultsApi';
import SingleAlertChartSection from './SingleAlertChartSection';
import HorizontalTimeline from './HorizontalTimeline';
import { RESOLVED_STATUSES } from '../AlertConfigs';
import { useBaseEntityInfo, usePagesContent } from '../../../Hooks/usePagesContent';
import AlertConditionPopup from '../Components/AlertConditionPopup';
import { getConditionRowBuilder } from './viewConditionRows';

const SingleAlertAnalysisDialog = ({ analyseData, open, onClose, fetchInfoFn, isPlatformAlert = false, viewConditionsMeta }) => {

    // For Platform/Smart Alerts the level is hidden, so omit the "LVx" suffix from
    // timeline/chart popup labels and show just the severity name.
    const buildSeverityLabel = (severityName, levelValue) =>
        isPlatformAlert ? `${severityName}` : `${severityName}, LV${levelValue}`;

    const { baseEntityTypePluralLable, baseEntityTypeLable, baseEntityType, baseEntityTypeIDLable } = useBaseEntityInfo();
    const pageContent = usePagesContent();
    const { battery, alertIncident } = analyseData;

    const { t } = useTranslation(['common', 'digital_twin'])
    const [isLoading, setIsLoading] = useState(false)
    const [timelineData, setTimelineData] = useState({})
    const [incidentInfo, setIncidentInfo] = useState({})
    const [isDiagnosticLoading, setIsDiagnosticLoading] = useState(false)

    const setupTimelineData = () => {
        let timeLineDateFormat = 'Do MMM YYYY h:mm A'
        let timeLineDateFormatRaw = 'YYYY-MM-DD HH:mm:ss'
        if (!alertIncident || !Object.keys(incidentInfo).length) {
            return false
        }

        const escalationPath = [...incidentInfo?.escalationPath].sort((a, b) => {
            return new Date(a.customerDBTime) - new Date(b.customerDBTime);
        });

        const firstEvent = escalationPath.find(element => element.changeType.toLowerCase() === 'created');

        let start = {
            date: getMetricTimeStamp(alertIncident.customerDBTimeInsert, timeLineDateFormatRaw),
            label: "Started",
            subLabel: getMetricTimeStamp(alertIncident.customerDBTimeInsert, timeLineDateFormat),
            severityLabel: buildSeverityLabel(alertIncident.severity, alertIncident.severityLevel),
            severity: alertIncident.severity,
        }
        if (firstEvent) {
            start = {
                date: getMetricTimeStamp(firstEvent.customerDBTime, timeLineDateFormatRaw),
                label: "Started",
                subLabel: getMetricTimeStamp(firstEvent.customerDBTime, timeLineDateFormat),
                severityLabel: buildSeverityLabel(firstEvent.severityName, firstEvent.severityLevelID),
                severity: firstEvent.severityName,
            }
        }

        let end = {
            date: getMetricTimeStamp(new Date(), timeLineDateFormatRaw),
            label: alertIncident.bmsAlertStatus,
            subLabel: getMetricTimeStamp(new Date(), timeLineDateFormat),
            severityLabel: buildSeverityLabel(alertIncident.severity, alertIncident.severityLevel),
            severity: alertIncident.severity,
        }
        if (RESOLVED_STATUSES.includes(alertIncident?.bmsAlertStatus)) {
            end = {
                ...end,
                date: getMetricTimeStamp(alertIncident.resolveCustomerDBTime, timeLineDateFormatRaw),
                subLabel: getMetricTimeStamp(alertIncident.resolveCustomerDBTime, timeLineDateFormat),
            }
        }

        let timelineSeparators = [];

        escalationPath.forEach((element, index) => {
            // skip created
            if (element.changeType.toLowerCase() === 'created') {
                return;
            }
            // skip last item
            if (index === escalationPath.length - 1) {
                return;
            }

            if (element.customerDBTime) {
                timelineSeparators.push({
                    date: getMetricTimeStamp(element.customerDBTime, timeLineDateFormatRaw),
                    label: element.bmsAlertStatus,
                    subLabel: getMetricTimeStamp(element.customerDBTime, timeLineDateFormat),
                    severityLabel: buildSeverityLabel(element.severityName, element.severityLevelID),
                    severity: element.severityName,
                    changeType: element.changeType,
                })
            }
        });

        setTimelineData({ start: start, timelineSeparators: timelineSeparators, end: end })
    }

    const getTagStyles = (key, value) => {
        return alertTagStyles[key][value?.toLowerCase()] || {}
    };

    const fetchDiagnosticInfo = async () => {
        setIncidentInfo({});
        setIsDiagnosticLoading(true);
        const fetchFn = fetchInfoFn || getDiagnosticInfo;
        const diagnosticRes = await fetchFn(alertIncident.incidentID);
        const data = diagnosticRes?.response?.data || {};
        setIncidentInfo(data);
        setIsDiagnosticLoading(false);
    }

    useEffect(() => {
        if (!open || !alertIncident) {
            return;
        }
        fetchDiagnosticInfo();
    }, [alertIncident, open])

    useEffect(() => {
        if (incidentInfo?.incidentID) {
            setupTimelineData();
        }
    }, [incidentInfo])


    // View Condition is fully meta-driven and domain-specific:
    //  - meta comes from an explicit `viewConditionsMeta` prop when the host page's
    //    context block isn't the right one (e.g. Digital Twin), else from the
    //    current page meta (`bmsAlertV2`/`platformAlertsV2` → `analyse.viewConditions`).
    //  - rows are built by the matching domain adapter so BMS faults and platform
    //    alerts never reshape each other's data.
    const resolvedViewConditions = viewConditionsMeta ?? pageContent?.metaInfo?.analyse?.viewConditions;
    const conditionColumns = resolvedViewConditions?.data ?? [];
    const conditionRows = resolvedViewConditions?.isVisible === true
        ? getConditionRowBuilder(isPlatformAlert)(incidentInfo)
        : [];

    return (
        <FullScreenDialog open={open} handleClose={onClose} loading={isLoading}>
            <Grid2 container justifyContent={'space-between'} alignItems={'center'} sx={{ pt: 3, pb: 1.5, px: 3 }}>
                <Grid2 container alignItems={'center'} gap={1} >
                    <Typography variant="fw500s16lh20c000000" sx={{ lineHeight: '20px' }}>{alertIncident?.incidentErrorName}</Typography>
                    <AlertConditionPopup
                        title={t(alertIncident?.incidentErrorName)}
                        heading={isPlatformAlert ? t('Alert Conditions') : t('Fault Conditions')}
                        triggerLabel={t('View Condition')}
                        columns={conditionColumns}
                        rows={conditionRows}
                        isVisible={resolvedViewConditions?.isVisible === true}
                        isLoading={isDiagnosticLoading}
                    />
                </Grid2>

                <Grid2 container justifyContent={'center'} alignItems={'center'} gap={1.8}>
                    <CloseButton onClick={onClose} />
                </Grid2>
            </Grid2>

            <Grid2 container alignItems={'center'} gap={1.2} sx={{ px: 3 }}>
                <CustomTag
                    text={toTitleCase(alertIncident?.bmsAlertStatus)}
                    fontColor={getTagStyles('bmsAlertStatus', alertIncident?.bmsAlertStatus).fontColor}
                    backgroundColor={getTagStyles('bmsAlertStatus', alertIncident?.bmsAlertStatus).backgroundColor}
                    fontSize={'12px'}
                    containerStyle={{
                        borderRadius: '12px',
                    }}
                />
                <CustomTag
                    text={alertIncident?.severity}
                    backgroundColor={getTagStyles('severity', alertIncident?.severity).backgroundColor}
                    fontColor={getTagStyles('severity', alertIncident?.severity).fontColor}
                    fontSize={'12px'}
                />
                {!isPlatformAlert && (
                    <CustomTag
                        text={`LV${alertIncident?.severityLevel}`}
                        backgroundColor={getTagStyles('severityLevel', 'default').backgroundColor}
                        fontColor={getTagStyles('severityLevel', 'default').fontColor}
                        fontSize={'12px'}
                    />
                )}

                <IDDisplayBox sx={{ py: 0 }}>
                    <Typography variant='fw400s12c494949' sx={{ lineHeight: '24px !important' }}>
                        {baseEntityTypeIDLable}:&nbsp;
                        {baseEntityType.toLowerCase() === "vehicle" ? battery?.licensePlateNo : battery?.deviceID}
                    </Typography>
                    <DotSeparator color="#000000" />
                    <Typography variant='fw500s12c000' sx={{ lineHeight: '16px' }}>
                        {t('Entity affected')}:&nbsp;
                        {alertIncident?.entityAffected?.toLowerCase() === 'tms'
                            ? 'TMS'
                            : toTitleCase(alertIncident?.entityAffectedDisplayID)}
                    </Typography>
                </IDDisplayBox>
            </Grid2>

            <Divider />
            <Grid2 container alignItems={'center'} sx={{ px: 4 }}>
                <Grid2 container alignItems={'center'} gap={1}>
                    <Typography variant="fw500s14lc000000">{t('Fault Lifecycle')}</Typography>
                    <IDDisplayBox sx={{ py: 0, px: 1 }}>
                        <CustomIcon name="clock" stroke="#686868" />
                        <Typography variant='fw500s12c686868' sx={{ lineHeight: '24px !important' }}>{getFormattedResolutionTime(incidentInfo?.alertDuration)} </Typography>
                    </IDDisplayBox>
                </Grid2>
                {timelineData?.start && <HorizontalTimeline data={timelineData} />}
            </Grid2>

            <Divider />

            <Grid2 container>
                <SingleAlertChartSection
                    incidentInfo={incidentInfo}
                    battery={battery}
                    alertIncident={alertIncident}
                    isPlatformAlert={isPlatformAlert}
                />
            </Grid2>
        </FullScreenDialog>
    );
};

export default SingleAlertAnalysisDialog;
